import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CommitteeMember, CommitteeType } from "@/types/governance";

export interface CommitteeDraft {
  name: string;
  description: string;
  ownerId: string | null;
  type: CommitteeType;
  memberIds: string[];
}

interface CommitteeFormProps {
  value: CommitteeDraft;
  members: CommitteeMember[];
  onChange: (next: CommitteeDraft) => void;
  parentName?: string;
  level: number;
}

export function CommitteeForm({ value, members, onChange, parentName, level }: CommitteeFormProps) {
  const [memberToAdd, setMemberToAdd] = useState("none");
  const ownerOptions = useMemo(
    () =>
      members.map((member) => ({
        label: `${member.name} (${member.designation})`,
        value: member.id,
      })),
    [members],
  );
  const selectedMembers = useMemo(
    () => members.filter((member) => value.memberIds.includes(member.id)),
    [members, value.memberIds],
  );

  const addMember = () => {
    if (memberToAdd === "none" || value.memberIds.includes(memberToAdd)) return;
    onChange({ ...value, memberIds: [...value.memberIds, memberToAdd] });
    setMemberToAdd("none");
  };

  const removeMember = (memberId: string) => {
    onChange({ ...value, memberIds: value.memberIds.filter((id) => id !== memberId) });
  };

  return (
    <div className="grid gap-4">
      <div>
        <Label htmlFor="committee-name">Committee Name</Label>
        <Input
          id="committee-name"
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
          placeholder={level === 1 ? "Board of Directors" : "Committee Name"}
        />
      </div>

      <div>
        <Label htmlFor="committee-description">Description</Label>
        <Textarea
          id="committee-description"
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          placeholder="Describe the scope and purpose of this committee."
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Committee Owner</Label>
          <Select
            value={value.ownerId ?? "none"}
            onValueChange={(next) => onChange({ ...value, ownerId: next === "none" ? null : next })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Assign committee owner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {ownerOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Committee Type</Label>
          <Select value={value.type} onValueChange={(next: CommitteeType) => onChange({ ...value, type: next })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standing">Standing</SelectItem>
              <SelectItem value="ad_hoc">Ad-hoc</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Committee Members</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Select value={memberToAdd} onValueChange={setMemberToAdd}>
            <SelectTrigger className="sm:flex-1">
              <SelectValue placeholder="Select member to add" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Select member</SelectItem>
              {members.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name} ({member.designation})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={addMember} disabled={memberToAdd === "none"}>
            Add Member
          </Button>
        </div>
        {selectedMembers.length > 0 ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {selectedMembers.map((member) => (
              <Badge key={member.id} variant="secondary" className="gap-1 pl-2 pr-1">
                <span>{member.name}</span>
                <button
                  type="button"
                  className="rounded p-0.5 hover:bg-black/10"
                  onClick={() => removeMember(member.id)}
                  aria-label={`Remove ${member.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No members added yet.</p>
        )}
      </div>

      {parentName ? (
        <p className="text-xs text-muted-foreground">
          Parent Committee: <span className="font-medium text-foreground">{parentName}</span>
        </p>
      ) : null}
    </div>
  );
}
